/**
 * Organization Scope Tab
 *
 * Configure which states, AHJs, trades, and features are enabled.
 */

import { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { logAdminAction, ACTION_DESCRIPTIONS, createDiff } from '@/lib/admin/auditLog';
import { getFloridaCounties } from '@/lib/supabase';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, MapPin, Briefcase, FileText, Shield } from 'lucide-react';
import { OrgScopeConfig } from '@/integrations/firebase/types';
import type { County } from '@/integrations/supabase/types';

interface OrgScopeTabProps {
  orgId: string;
  scopeConfig: OrgScopeConfig | null;
  onUpdate: (config: OrgScopeConfig) => void;
}

// Available options
const STATES = [
  { code: 'FL', name: 'Florida' },
  { code: 'TX', name: 'Texas' },
  { code: 'CA', name: 'California' },
  { code: 'GA', name: 'Georgia' },
];

// Counties/AHJs are loaded dynamically from Supabase

const TRADES = [
  { id: 'hvac', name: 'HVAC' },
  { id: 'electrical', name: 'Electrical' },
  { id: 'plumbing', name: 'Plumbing' },
  { id: 'roofing', name: 'Roofing' },
  { id: 'solar', name: 'Solar' },
  { id: 'gas', name: 'Gas' },
];

const DOC_TYPES = [
  { id: 'permit_app', name: 'Permit Application' },
  { id: 'manual_j', name: 'Manual J' },
  { id: 'spec_sheet', name: 'Equipment Spec Sheet' },
  { id: 'noa', name: 'NOA (Notice of Acceptance)' },
  { id: 'pe_letter', name: 'PE Letter' },
  { id: 'energy_calcs', name: 'Energy Calculations' },
];

const RULE_PACKS = [
  { id: 'hvac_manualj_pack', name: 'HVAC Manual J Pack' },
  { id: 'hvhz_noa_pack', name: 'HVHZ NOA Pack' },
  { id: 'electrical_load_calc', name: 'Electrical Load Calc' },
  { id: 'plumbing_basic', name: 'Plumbing Basic' },
  { id: 'commercial_energy', name: 'Commercial Energy' },
];

export function OrgScopeTab({ orgId, scopeConfig, onUpdate }: OrgScopeTabProps) {
  const { user } = useAuth();
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [counties, setCounties] = useState<County[]>([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [formData, setFormData] = useState({
    enabledStates: scopeConfig?.enabledStates || [],
    enabledAhjs: scopeConfig?.enabledAhjs || [],
    projectTypes: scopeConfig?.projectTypes || [],
    tradePacks: scopeConfig?.tradePacks || [],
    docTypesEnabled: scopeConfig?.docTypesEnabled || [],
    rulePacksEnabled: scopeConfig?.rulePacksEnabled || [],
  });

  // Fetch Florida counties from Supabase when FL is selected
  useEffect(() => {
    const fetchCounties = async () => {
      // Only load if Florida is selected
      if (!formData.enabledStates.includes('FL')) {
        setCounties([]);
        return;
      }

      setLoadingCounties(true);
      try {
        const { data, error } = await getFloridaCounties();
        if (error) {
          console.error('Error fetching counties:', error);
          return;
        }
        setCounties(data || []);
      } catch (err) {
        console.error('Error fetching counties:', err);
      } finally {
        setLoadingCounties(false);
      }
    };

    fetchCounties();
  }, [formData.enabledStates]);

  const toggleItem = (field: keyof typeof formData, item: string) => {
    setFormData((prev) => {
      const current = prev[field] as string[];
      const updated = current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item];
      return { ...prev, [field]: updated };
    });
  };

  const handleSave = async () => {
    if (!user?.uid || !adminUser) return;

    try {
      setSaving(true);

      const updatedConfig: OrgScopeConfig = {
        orgId,
        enabledStates: formData.enabledStates,
        enabledAhjs: formData.enabledAhjs,
        projectTypes: formData.projectTypes as ('residential' | 'commercial')[],
        tradePacks: formData.tradePacks,
        docTypesEnabled: formData.docTypesEnabled,
        rulePacksEnabled: formData.rulePacksEnabled,
        updatedAt: serverTimestamp() as any,
        updatedBy: user.uid,
      };

      await updateDoc(doc(db, 'org_scope_configs', orgId), updatedConfig as Record<string, any>);

      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: 'SET_SCOPE',
        targetOrgId: orgId,
        description: ACTION_DESCRIPTIONS.SET_SCOPE(orgId),
        diff: scopeConfig ? createDiff(scopeConfig, updatedConfig) : undefined,
      });

      onUpdate(updatedConfig);

      toast({
        title: 'Scope Updated',
        description: 'Configuration has been saved',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-white mb-1">Scope Configuration</h2>
        <p className="text-sm text-gray-400">
          Define what states, AHJs, trades, and features are available to this organization
        </p>
      </div>

      {/* States */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Enabled States</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATES.map((state) => (
            <button
              key={state.code}
              onClick={() => toggleItem('enabledStates', state.code)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                formData.enabledStates.includes(state.code)
                  ? 'bg-[hsl(220,70%,50%)] text-white'
                  : 'bg-[hsl(220,15%,15%)] text-gray-400 hover:bg-[hsl(220,15%,20%)]'
              }`}
            >
              {state.code} - {state.name}
            </button>
          ))}
        </div>
      </AdminCard>

      {/* Counties/AHJs */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Enabled Counties</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Leave empty to enable ALL counties in selected states. Select specific counties to restrict access.
        </p>
        {formData.enabledAhjs.length === 0 && formData.enabledStates.length > 0 && (
          <div className="mb-3 px-3 py-2 rounded-md bg-[hsl(220,70%,50%)]/10 border border-[hsl(220,70%,50%)]/30">
            <p className="text-xs text-[hsl(220,70%,70%)]">
              All counties in {formData.enabledStates.join(', ')} are enabled
            </p>
          </div>
        )}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loadingCounties ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
              <span className="ml-2 text-sm text-gray-500">Loading counties...</span>
            </div>
          ) : counties.length > 0 ? (
            counties.map((county) => (
              <label
                key={county.id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-[hsl(220,15%,12%)] cursor-pointer"
              >
                <Checkbox
                  checked={formData.enabledAhjs.includes(county.name.toLowerCase().replace(/\s+/g, '-'))}
                  onCheckedChange={() => toggleItem('enabledAhjs', county.name.toLowerCase().replace(/\s+/g, '-'))}
                />
                <span className="text-sm text-white">{county.name} County</span>
                <Badge variant="outline" className="text-xs border-[hsl(220,15%,30%)] text-gray-500">
                  FL
                </Badge>
              </label>
            ))
          ) : formData.enabledStates.length === 0 ? (
            <p className="text-sm text-gray-500">Select states first to see available counties</p>
          ) : (
            <p className="text-sm text-gray-500">No counties available for selected states</p>
          )}
        </div>
        {counties.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            {counties.length} counties available
          </p>
        )}
      </AdminCard>

      {/* Project Types */}
      <AdminCard>
        <h3 className="text-sm font-medium text-white mb-4">Project Types</h3>
        <div className="flex gap-4">
          {(['residential', 'commercial'] as const).map((type) => (
            <label
              key={type}
              className="flex items-center gap-3 cursor-pointer"
            >
              <Checkbox
                checked={formData.projectTypes.includes(type)}
                onCheckedChange={() => toggleItem('projectTypes', type)}
              />
              <span className="text-sm text-white capitalize">{type}</span>
            </label>
          ))}
        </div>
      </AdminCard>

      {/* Trades */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Trade Packs</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {TRADES.map((trade) => (
            <button
              key={trade.id}
              onClick={() => toggleItem('tradePacks', trade.id)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                formData.tradePacks.includes(trade.id)
                  ? 'bg-[hsl(150,60%,40%)] text-white'
                  : 'bg-[hsl(220,15%,15%)] text-gray-400 hover:bg-[hsl(220,15%,20%)]'
              }`}
            >
              {trade.name}
            </button>
          ))}
        </div>
      </AdminCard>

      {/* Document Types */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Document Types</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {DOC_TYPES.map((docType) => (
            <label
              key={docType.id}
              className="flex items-center gap-3 p-2 rounded-md hover:bg-[hsl(220,15%,12%)] cursor-pointer"
            >
              <Checkbox
                checked={formData.docTypesEnabled.includes(docType.id)}
                onCheckedChange={() => toggleItem('docTypesEnabled', docType.id)}
              />
              <span className="text-sm text-white">{docType.name}</span>
            </label>
          ))}
        </div>
      </AdminCard>

      {/* Rule Packs */}
      <AdminCard>
        <h3 className="text-sm font-medium text-white mb-4">Rule Packs</h3>
        <div className="space-y-2">
          {RULE_PACKS.map((pack) => (
            <label
              key={pack.id}
              className="flex items-center gap-3 p-2 rounded-md hover:bg-[hsl(220,15%,12%)] cursor-pointer"
            >
              <Checkbox
                checked={formData.rulePacksEnabled.includes(pack.id)}
                onCheckedChange={() => toggleItem('rulePacksEnabled', pack.id)}
              />
              <span className="text-sm text-white">{pack.name}</span>
            </label>
          ))}
        </div>
      </AdminCard>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
